import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from 'components/formComponents/Icons/WarningIcon';
import styles from './ReportDismissedSection.module.scss';
import { CYPRESS_DATA } from './constants';
import EmployeeField from './EmployeeField';
import { EMPLOYMENT_TYPE_OPTIONS, EmployeeRow } from './types';
import { formatSocialSecurityNumber } from './utils/socialSecurityNumber';
import { RegisterDismissedUiTexts, replaceTemplateTokens } from './texts';

type Props = {
  row: EmployeeRow;
  texts: RegisterDismissedUiTexts;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onRemove: () => void;
};

const TerminatedEmployeeMobileCard = ({
  row,
  texts,
  isExpanded,
  onToggleExpanded,
  onRemove,
}: Props) => {
  const prefix = `terminatedEmployees.${row.formIndex}`;
  const name =
    `${row.employee.firstName} ${row.employee.lastName}`.trim() || 'Ny person';
  const rowWarning = row.warnings.find((warning) => !warning.field);
  const startDateWarning = row.warnings.find(
    (warning) => warning.field === 'startDate'
  );
  const noNoticePeriodWarning = row.warnings.find(
    (warning) => warning.field === 'terminationDate'
  );
  const endDateWarning = row.warnings.find(
    (warning) => warning.field === 'endDate'
  );

  return (
    <li
      className={styles.mobileCard}
      data-cy={CYPRESS_DATA.mobileCard}
      data-status={row.status}
    >
      <div className={styles.mobileCardHeader}>
        <button
          type="button"
          className={styles.mobileCardToggle}
          onClick={onToggleExpanded}
          aria-expanded={isExpanded}
          data-cy={CYPRESS_DATA.mobileCardToggle}
        >
          {row.status === 'error' && (
            <ErrorOutlineIcon className={styles.statusError} fontSize="small" />
          )}
          {row.status === 'warning' && (
            <WarningIcon className={styles.statusWarning} fontSize="small" />
          )}
          {row.status === 'valid' && (
            <CheckIcon className={styles.statusValid} fontSize="small" />
          )}
          <span className={styles.mobileCardName}>{name}</span>
          <span>
            {formatSocialSecurityNumber(row.employee.socialSecurityNumber)}
          </span>
          {isExpanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className={styles.mobileCardBody}>
          <EmployeeField
            name={`${prefix}.socialSecurityNumber`}
            label={texts.columnSsn}
            error={row.errors.socialSecurityNumber}
          />
          <EmployeeField
            name={`${prefix}.firstName`}
            label={texts.columnFirstName}
            error={row.errors.firstName}
          />
          <EmployeeField
            name={`${prefix}.lastName`}
            label={texts.columnLastName}
            error={row.errors.lastName}
          />
          <EmployeeField
            name={`${prefix}.employmentType`}
            label={texts.columnEmploymentType}
            options={EMPLOYMENT_TYPE_OPTIONS}
            error={row.errors.employmentType}
          />
          <EmployeeField
            name={`${prefix}.startDate`}
            label={texts.columnStartDate}
            type="date"
            error={row.errors.startDate}
            warning={startDateWarning?.message}
          />
          <EmployeeField
            name={`${prefix}.terminationDate`}
            label={texts.columnTerminationDate}
            type="date"
            error={row.errors.terminationDate}
          />
          <EmployeeField
            name={`${prefix}.endDate`}
            label={texts.columnEndDate}
            type="date"
            error={row.errors.endDate}
            warning={endDateWarning?.message ?? noNoticePeriodWarning?.message}
          />
          <EmployeeField
            name={`${prefix}.email`}
            label={texts.columnEmail}
            type="email"
            error={row.errors.email}
            warning={rowWarning?.message}
          />
          <EmployeeField
            name={`${prefix}.phone`}
            label={texts.columnPhone}
            type="tel"
            error={row.errors.phone}
          />
          <div className={styles.mobileCardActions}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={onRemove}
              aria-label={replaceTemplateTokens(texts.removeRowLabel, {
                name,
              })}
              data-cy={CYPRESS_DATA.removeRow}
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

export default TerminatedEmployeeMobileCard;
